import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { orpc } from '@/lib/api/orpc';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { isSafeRedirectTo } from '@/lib/oauth2';
import { QUERY_PRESETS } from '@/lib/query';
import { navigateToUrl } from '@/utils/navigateToUrl';

/**
 * `softwareContract`（@standandforce/api-contract）由来のルート。
 * ローカル Contract 時代の `get` / `update` / `startAdminConsent` の呼び名を保つ。
 */
const mdmDeviceSync = {
  get: orpc.software.integrationMdmDeviceSync,
  startAdminConsent: orpc.software.integrationMdmAdminConsentStart
};

/**
 * エラー表示はセクション内で完結させるため、グローバルエラーダイアログを抑止する。
 * MDM の取得・更新失敗で連携設定タブ全体をブロックさせない。
 */
const LOCAL_ERROR_META = {
  disableGlobalErrorHandling: true
} as const;

/**
 * MDM デバイス同期設定（Admin Consent 経路）の queryOptions。
 *
 * queryKey / queryFn は oRPC が Contract から生成する（手動 Query Key Factory は持たない）。
 * meta は factory 側に置き、hook / prefetch いずれでも抑止が漏れないようにする。
 */
export const mdmDeviceSyncSettingsQueryOptions = (softwareId: string) => ({
  ...mdmDeviceSync.get.queryOptions({
    input: {
      softwareId
    }
  }),
  ...QUERY_PRESETS.detail(),
  meta: LOCAL_ERROR_META
});

/**
 * MDM デバイス同期設定（Admin Consent 経路）の取得 hook。
 *
 * 呼び出し側は `isMdmDeviceSyncEligible` で表示対象と判定された画面に限る
 * （子 Intune / case c では MDM API を呼ばないこと。spec 必須要件）。
 */
export const useMdmDeviceSyncSettings = (softwareId: string) => {
  return useQuery(mdmDeviceSyncSettingsQueryOptions(softwareId));
};

/**
 * Admin Consent 開始（POST mdm-admin-consent/start）mutation hook。
 * 取得した Consent URL へハードナビゲーションする。
 */
export const useMdmAdminConsentStartMutator = () => {
  const {
    t
  } = useTranslation(defaultNS);
  const showConnectError = () => {
    toast({
      description: t($ => $.pages.profile.app.settings.mdmDeviceSync.toast.connectError),
      variant: 'destructive'
    });
  };
  return useMutation({
    ...mdmDeviceSync.startAdminConsent.mutationOptions(),
    onSuccess: ({
      url
    }) => {
      // 外部許可遷移: API 由来の URL は http(s) のみ許可（secure-navigation ルール）
      if (isSafeRedirectTo(url)) {
        navigateToUrl(url);
        return;
      }
      showConnectError();
    },
    onError: showConnectError,
    meta: LOCAL_ERROR_META
  });
};